import { selectAiReviewStats } from "@/store/ai-asset-review/ai-asset-review-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  blue,
  FaroText,
  FontWeights,
  green,
  grey,
  red,
  yellow,
} from "@faro-lotv/flat-ui";
import { Box, Divider, LinearProgress, Stack } from "@mui/material";
import { memo, type JSX } from "react";

type StatusBadgeProps = {
  count: number;
  variant: "approved" | "rejected" | "remaining";
};

const BADGE_HEIGHT = 22;

/** @returns capsule-shaped badge with count */
function StatusBadge({ count, variant }: StatusBadgeProps): JSX.Element {
  const colors = {
    approved: {
      bg: green[50],
      border: green[200],
      text: green[700],
    },
    rejected: {
      bg: red[50],
      border: red[200],
      text: red[700],
    },
    remaining: {
      bg: yellow[50],
      border: yellow[200],
      text: yellow[700],
    },
  };

  const { bg, border, text } = colors[variant];

  return (
    <Box
      component="div"
      sx={{
        minWidth: BADGE_HEIGHT,
        height: BADGE_HEIGHT,
        borderRadius: BADGE_HEIGHT / 2,
        backgroundColor: bg,
        border: 0.5,
        borderColor: border,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1,
      }}
    >
      <FaroText
        variant="bodyXS"
        sx={{
          color: text,
          fontWeight: FontWeights.SemiBold,
        }}
      >
        {count}
      </FaroText>
    </Box>
  );
}

/** @returns card displaying review progress and statistics */
export const AiReviewStatusCard = memo(
  function AiReviewStatusCard(): JSX.Element {
    const stats = useAppSelector(selectAiReviewStats);

    return (
      <Box component="div" sx={{ width: "100%" }}>
        <FaroText
          variant="heading14"
          color={grey[800]}
          fontWeight={FontWeights.SemiBold}
          sx={{ mb: 2, display: "block" }}
        >
          Review status
        </FaroText>

        {/* Statistics */}
        <Stack>
          {/* Total */}
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.5,
            }}
          >
            <FaroText variant="bodyS" color={grey[600]}>
              Total
            </FaroText>
            <FaroText variant="bodyS" color={grey[800]}>
              {stats.total}
            </FaroText>
          </Stack>

          <Divider sx={{ borderColor: grey[100] }} />

          {/* Approved */}
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.5,
            }}
          >
            <FaroText variant="bodyS" color={grey[600]}>
              Approved
            </FaroText>
            <StatusBadge count={stats.approved} variant="approved" />
          </Stack>

          <Divider sx={{ borderColor: grey[100] }} />

          {/* Rejected */}
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.5,
            }}
          >
            <FaroText variant="bodyS" color={grey[600]}>
              Rejected
            </FaroText>
            <StatusBadge count={stats.rejected} variant="rejected" />
          </Stack>

          <Divider sx={{ borderColor: grey[100] }} />

          {/* Remaining */}
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.5,
            }}
          >
            <FaroText variant="bodyS" color={grey[600]}>
              Remaining
            </FaroText>
            <StatusBadge count={stats.remaining} variant="remaining" />
          </Stack>

          {/* Progress Bar */}
          <Box component="div" sx={{ pt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={stats.progress}
              sx={{
                height: 4,
                borderRadius: 0,
                backgroundColor: grey[200],
                "& .MuiLinearProgress-bar": {
                  backgroundColor: blue[500],
                  backgroundImage: "none",
                  borderRadius: 0,
                },
              }}
            />
          </Box>
        </Stack>

        {/* Note */}
        <FaroText
          variant="bodyS"
          color={grey[600]}
          sx={{ mt: 3, display: "block" }}
        >
          Note: you can interrupt your review and continue in another moment.
        </FaroText>
      </Box>
    );
  },
);
